import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../lib/utils";
import {
  FileText,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  Copy,
  RefreshCw,
  GitBranch,
  Share2,
  ShieldCheck,
  CheckCircle,
  Bot,
  Server,
} from "lucide-react";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../ui/dialog";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayFilterBar from "../components/GatewayFilterBar";
import GatewayStatusBadge from "../components/GatewayStatusBadge";
import GatewayModuleBadge from "../components/GatewayModuleBadge";
import GatewayProtocolBadge from "../components/GatewayProtocolBadge";
import GatewayCapabilityTypeBadge from "../components/GatewayCapabilityTypeBadge";
import GatewaySeverityDot from "../components/GatewaySeverityDot";
import GatewayJsonViewer from "../components/GatewayJsonViewer";
import GatewayTimeline from "../components/GatewayTimeline";
import GatewayTraceTimeline from "../components/GatewayTraceTimeline";
import GatewayOboChain from "../components/GatewayOboChain";
import GatewayOboReceipt from "../components/GatewayOboReceipt";
import GatewayTraceDag from "../components/GatewayTraceDag";
import {
  GatewayAuditApi,
  GatewayStsApi,
  GatewayPostProcessorApi,
} from "../Api/gatewayApi";
import {
  formatTimestamp,
  formatDuration,
  durationClass,
  callerTypeLabel,
  copyToClipboard,
  apiError,
} from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import type { AuditLogEntry, AuditStats, AuditFilters } from "../interfaces/gatewayInterfaces";
import "../../ManagementDialog.css";
import CardPagination from "../../CardPagination";
import "../AgenticGateway.css";

// Egress-reprocess availability from a correlation/trace's events (mirrors the OG audit reprocess gating).
function chainReprocess(events: any[]): { can: boolean; label: string } {
  const skill = events.some((e) => e.eventType === "ORCHESTRATION_RESPONSE_RETURNED" && e.capabilityType === "SKILL");
  const prompt = events.some((e) => e.eventType === "ORCHESTRATION_RESPONSE_RETURNED" && e.capabilityType === "PROMPT");
  const resource = events.some((e) => e.eventType === "CLIENT_RESOURCE_READ");
  const tool = events.some((e) => e.eventType === "CLIENT_TOOL_INVOCATION");
  const label = skill ? "Reprocess skill response" : prompt ? "Reprocess prompt response" : resource ? "Reprocess resource response" : "Reprocess tool response";
  return { can: skill || prompt || resource || tool, label };
}
function traceReprocess(events: any[]): boolean {
  return events.some(
    (e) =>
      e.eventType === "CLIENT_TOOL_INVOCATION" ||
      e.eventType === "CLIENT_RESOURCE_READ" ||
      (e.eventType === "ORCHESTRATION_RESPONSE_RETURNED" && (e.capabilityType === "SKILL" || e.capabilityType === "PROMPT"))
  );
}
// A small "go to Processed Data, pre-filtered" + reprocess link pair for the audit chain/trace headers.
function postProcNav(navigate: ReturnType<typeof useNavigate>, q: { correlationId?: string; traceId?: string }) {
  const p = new URLSearchParams();
  if (q.correlationId) p.set("correlationId", q.correlationId);
  if (q.traceId) p.set("traceId", q.traceId);
  navigate(`/agentic-gateway/postproc?${p.toString()}`);
}
const chainLinkCls = "inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 transition-colors";


// ── Correlation Chain: one connection row (icon + label | copyable session id + hint) ──
// Label is a fixed-width left column; the id / copy / hint live in a right column so the
// hint stays tidily in place (never wraps back under the label).
function ConnSessionRow({
  icon,
  label,
  sid,
  hint,
  stateless,
  onCopy,
}: {
  icon: React.ReactNode;
  label: string;
  sid?: string | null;
  hint?: string;
  stateless?: boolean;
  onCopy: (t: string) => void;
}) {
  const shown = sid && sid.length > 44 ? `${sid.slice(0, 44)}…` : sid;
  return (
    <div className="flex items-start gap-3">
      <span className="w-40 shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="shrink-0">{icon}</span>
        {label}
      </span>
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        {stateless ? (
          <span className="text-xs text-muted-foreground/70 italic">no session — authenticated per request</span>
        ) : !sid ? (
          <span className="text-xs text-muted-foreground/70">— not reached —</span>
        ) : (
          <>
            <code className="text-xs text-primary font-mono break-all">{shown}</code>
            <button
              onClick={() => onCopy(sid)}
              title="Copy session id"
              className="p-0.5 text-muted-foreground hover:text-foreground shrink-0"
            >
              <Copy className="w-3 h-3" />
            </button>
            {hint && <span className="text-[11px] text-muted-foreground/70">{hint}</span>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Request Trace helpers (mirror the OG audit.js trace header) ──
function traceOutcome(events: any[]): { label: string; cls: string } {
  if (events.some((e) => e.status === "ERROR" || e.status === "FAILURE"))
    return { label: "ERROR", cls: "text-red-600 dark:text-red-400" };
  if (events.some((e) => e.pdpDecision === "DENY" || e.status === "DENIED"))
    return { label: "DENIED", cls: "text-orange-600 dark:text-orange-400" };
  return { label: "SUCCESS", cls: "text-emerald-600 dark:text-emerald-400" };
}

function traceActChain(events: any[]): any[] | null {
  for (const e of events) {
    const p = e.responsePayload || e.response_payload;
    const chain = p?.act_chain || p?.actChain;
    if (Array.isArray(chain) && chain.length) return chain;
  }
  return null;
}

function TraceStat({ label, value, cls }: { label: string; value: React.ReactNode; cls?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("text-sm font-bold text-foreground", cls)}>{value}</div>
    </div>
  );
}

function TraceActChain({ chain }: { chain: any[] }) {
  return (
    <div className="flex items-center flex-wrap gap-y-1">
      {chain.map((pr, i) => {
        const label = pr.username || pr.workload_id || pr.clientId || (pr.id ? `${String(pr.id).slice(0, 8)}…` : "?");
        return (
          <React.Fragment key={i}>
            {i > 0 && <span className="mx-1.5 text-muted-foreground">→</span>}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ag-surface text-xs">
              <strong className="text-foreground font-semibold">{label}</strong>
              {pr.type && <span className="text-[10px] text-muted-foreground">{pr.type}</span>}
              {/* Identity source the actor proved with — KEYCLOAK today, SPIFFE once deployed */}
              {pr.identity_source && (
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70" title="Identity source">
                  {pr.identity_source}
                </span>
              )}
              {pr.verified ? (
                <CheckCircle className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400" />
              )}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}


interface QueryParams {
  page: number;
  size: number;
  sort: string;
  module?: string | string[];
  eventType?: string | string[];
  status?: string | string[];
  severity?: string | string[];
  agentName?: string | string[];
  serverName?: string | string[];
  tokenType?: string | string[];
  protocol?: string | string[];
  capabilityType?: string | string[];
  userIdentity?: string | string[];
  capabilityName?: string;
  fromDate?: string;
  toDate?: string;
  sourceIp?: string;
  traceId?: string;
  search?: string;
}

interface PagedResult {
  content: AuditLogEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}


interface TimelineSlot {
  timestamp: Date;
  count: number;
}

function HourlyTimeline({ data }: { data: Array<{ timestamp?: string; hour?: string; count: number }> }) {
  const slots: TimelineSlot[] = [];
  const hourMap = new Map<number, number>();

  (data || []).forEach((d) => {
    const raw = d.timestamp || d.hour;
    if (raw) {
      const dt = new Date(raw);
      const key = new Date(
        dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours()
      ).getTime();
      hourMap.set(key, (hourMap.get(key) || 0) + (d.count || 0));
    }
  });

  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const slotDate = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - i
    );
    slots.push({ timestamp: slotDate, count: hourMap.get(slotDate.getTime()) || 0 });
  }

  const maxCount = Math.max(...slots.map((s) => s.count), 1);
  const firstLabel = slots[0].timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastLabel = slots[slots.length - 1].timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  return (
    <div className="ag-card p-4">
      <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">
        Event Activity (Last 24 Hours)
      </p>
      <div className="flex items-end gap-0.5 h-20">
        {slots.map((s, i) => {
          const pct = s.count > 0 ? Math.max((s.count / maxCount) * 100, 4) : 2;
          const label = s.timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
          return (
            <div
              key={i}
              className="group relative flex-1 flex flex-col justify-end"
              style={{ height: "100%" }}
            >
              <div
                className={cn(
                  "w-full rounded-t-sm transition-all cursor-default",
                  s.count > 0
                    ? "bg-primary/50 hover:bg-primary/60"
                    : "bg-primary/10"
                )}
                style={{ height: `${pct}%` }}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 whitespace-nowrap">
                <div className="bg-card border border-border rounded-lg px-2 py-1 text-[10px] text-foreground/80 shadow-[0_8px_24px_-6px_rgb(15_23_42/0.18)]">
                  {label}: {s.count} events
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-muted-foreground/70">{firstLabel}</span>
        <span className="text-[10px] text-muted-foreground/70">{lastLabel}</span>
      </div>
    </div>
  );
}


function ExpandedLogRow({
  log,
  onShowCorrelation,
}: {
  log: AuditLogEntry;
  onShowCorrelation: (id: string) => void;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const reprocessTraceH = async (traceId: string) => {
    try {
      const res = await GatewayPostProcessorApi.reprocessTrace(traceId);
      toast({ title: `Trace reprocessed — ${res?.updated || 0} updated, ${res?.skipped || 0} skipped` });
    } catch (e: any) {
      toast({ title: "Reprocess failed", description: apiError(e), variant: "destructive" });
    }
  };
  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    toast({ title: "Copied to clipboard" });
  };
  // current backend field names, with legacy fallbacks
  const callerVal = log.tokenType ?? log.callerType;
  const humanUserVal = log.userIdentity ?? log.humanUser;
  const oauthClientVal = log.agentClientId ?? log.oauth2Client;
  const sourceIpVal = log.sourceIp ?? log.ipAddress;
  const caller = callerTypeLabel(callerVal);
  const traceId = (log as any).traceId;

  // OBO Token Receipt
  const [oboOpen, setOboOpen] = useState(false);
  const [oboData] = useState<any>(null);
  const [oboLoading] = useState(false);
  const [oboMissing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const handleRevoke = async () => {
    if (!oboData?.jti) return;
    setRevoking(true);
    try {
      await GatewayStsApi.revoke({ jti: oboData.jti, expiresAt: oboData.expiresAt, reason: "Revoked from audit log" });
      toast({ title: "Delegation token revoked" });
    } catch (e: any) {
      toast({ title: "Revoke failed", description: e?.message, variant: "destructive" });
    } finally {
      setRevoking(false);
    }
  };

  // Trace chain
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceData, setTraceData] = useState<any>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  // Governance-trail DAG (View DAG)
  const [dagOpen, setDagOpen] = useState(false);
  const openTrace = async () => {
    if (!traceId) return;
    setTraceOpen(true);
    setTraceLoading(true);
    setTraceData(null);
    try {
      const data = await GatewayAuditApi.trace(traceId);
      setTraceData(data);
    } catch (e: any) {
      toast({ title: "Failed to load trace", description: e?.message, variant: "destructive" });
    } finally {
      setTraceLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4 ag-surface border-t border-border">
      {log.comment && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Note</p>
          <p className="text-sm text-foreground/90">{log.comment}</p>
        </div>
      )}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Identifiers</p>
        <div className="flex flex-wrap gap-3">
          {log.agentName && (
            <IdItem label="Agent" value={log.agentName} onCopy={handleCopy} />
          )}
          {log.sessionId && (
            <IdItem label="Session ID" value={log.sessionId} onCopy={handleCopy} />
          )}
          {log.correlationId && (
            <IdItem label="Correlation ID" value={log.correlationId} onCopy={handleCopy} />
          )}
          {traceId && (
            <>
              <IdItem label="Trace ID" value={traceId} onCopy={handleCopy} />
              <button
                onClick={openTrace}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
              >
                <GitBranch className="w-3 h-3" /> View Trace
              </button>
              <button
                onClick={() => setDagOpen(true)}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
              >
                <Share2 className="w-3 h-3" /> View OBO Chain
              </button>
            </>
          )}
          {log.requestId && (
            <IdItem label="Request ID" value={log.requestId} onCopy={handleCopy} />
          )}
        </div>
      </div>

      {(callerVal || log.authMethod || humanUserVal || oauthClientVal || sourceIpVal || log.authIdentity) && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Authentication</p>
          <div className="flex flex-wrap gap-3">
            {callerVal && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Caller:</span>
                <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", caller.color)}>
                  {caller.label}
                </span>
              </div>
            )}
            {log.authMethod && (
              <IdItem label="Auth Method" value={log.authMethod} onCopy={handleCopy} />
            )}
            {humanUserVal && (
              <IdItem label="Human User" value={humanUserVal} onCopy={handleCopy} />
            )}
            {log.authIdentity && (
              <IdItem label="Subject" value={log.authIdentity} onCopy={handleCopy} />
            )}
            {oauthClientVal && (
              <IdItem label="OAuth2 Client" value={oauthClientVal} onCopy={handleCopy} />
            )}
            {sourceIpVal && (
              <IdItem label="IP Address" value={sourceIpVal} onCopy={handleCopy} />
            )}
          </div>
        </div>
      )}

      {log.requestPayload && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Request Payload</p>
          <GatewayJsonViewer
            data={log.requestPayload}
            collapsed={false}
            maxHeight="320px"
          />
        </div>
      )}

      {log.responsePayload && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Response Payload</p>
          <GatewayJsonViewer
            data={log.responsePayload}
            collapsed={false}
            maxHeight="320px"
            unwrap
          />
        </div>
      )}

      {(log.status === "ERROR" || log.status === "FAILURE" || log.errorMessage) && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400 font-medium">Error Details</p>
          {(log as any).errorCode && (
            <p className="text-xs text-muted-foreground">
              Code: <span className="text-foreground/80">{(log as any).errorCode}</span>
            </p>
          )}
          {log.errorMessage && (
            <p className="text-xs text-red-300">{log.errorMessage}</p>
          )}
          {(log as any).errorData && (
            <div className="pt-1">
              <GatewayJsonViewer data={(log as any).errorData} collapsed={false} maxHeight="200px" />
            </div>
          )}
        </div>
      )}

      {/* OBO Token Receipt dialog */}
      <Dialog open={oboOpen} onOpenChange={setOboOpen}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <ShieldCheck />
              </span>
              <div>
                <DialogTitle>Token Chain</DialogTitle>
                <DialogDescription>
                  On-behalf-of delegation token receipt for correlation{" "}
                  <code className="text-primary font-mono">{log.correlationId}</code>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            {oboLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading receipt…</div>
            ) : oboMissing ? (
              <p className="text-sm text-muted-foreground leading-relaxed py-4">
                No on-behalf-of (OBO) token was minted for this request — the gateway forwarded an
                agent-provided or config-based token instead, so there is no delegation receipt to show.
              </p>
            ) : oboData ? (
              <div className="space-y-3">
                <GatewayOboReceipt receipt={oboData} />
                {oboData.jti && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRevoke}
                      disabled={revoking}
                      className="border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                    >
                      {revoking ? "Revoking…" : "Revoke token"}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Request trace dialog */}
      <Dialog open={traceOpen} onOpenChange={setTraceOpen}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-3xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <GitBranch />
              </span>
              <div>
                <DialogTitle>Request Trace</DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            {traceLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading trace…</div>
            ) : traceData ? (
              (() => {
                const events: any[] = Array.isArray(traceData) ? traceData : (traceData.events || traceData.timeline || []);
                if (!events.length) return <div className="py-8 text-center text-sm text-muted-foreground">No trace events</div>;
                const outcome = traceOutcome(events);
                const toolEvt = events.find((e) => e.capabilityName);
                const tool = toolEvt?.capabilityName || "—";
                const first = new Date(events[0].timestamp).getTime();
                const last = new Date(events[events.length - 1].timestamp).getTime();
                const spanMs = isNaN(first) || isNaN(last) ? 0 : Math.max(0, last - first);
                const chain = traceActChain(events);
                return (
                  // min-w-0 lets this grid item shrink to the modal's fixed width so the OBO row's
                  // overflow-x-auto actually clips+scrolls instead of stretching the whole window.
                  <div className="space-y-3.5 min-w-0">
                    {/* summary */}
                    <div className="flex flex-wrap items-center gap-x-10 gap-y-3 rounded-xl border border-border ag-surface px-5 py-3.5">
                      <TraceStat label="Outcome" value={outcome.label} cls={outcome.cls} />
                      <TraceStat label="Tool" value={<span className="font-mono">{tool}</span>} />
                      <TraceStat label="Events" value={events.length} />
                      <TraceStat label="Span" value={formatDuration(spanMs)} />
                    </div>
                    {/* egress post-processor: jump to Processed Data (pre-filtered) / reprocess this trace */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" className={chainLinkCls} onClick={() => postProcNav(navigate, { traceId })}>
                        <ShieldCheck className="w-3 h-3" /> Processed (Trace)
                      </button>
                      {traceReprocess(events) && (
                        <button type="button" className={chainLinkCls} onClick={() => reprocessTraceH(traceId)}>
                          <RefreshCw className="w-3 h-3" /> Reprocess entire trace
                        </button>
                      )}
                    </div>
                    {/* delegation chain */}
                    {chain && chain.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                          Acting on behalf of — delegation chain
                        </div>
                        <TraceActChain chain={chain} />
                      </div>
                    )}
                    <GatewayOboChain events={events} />
                    {/* trace id */}
                    <p className="text-[11px] text-muted-foreground">
                      Trace ID: <code className="text-primary font-mono break-all">{traceId}</code> — the full request lifecycle, root to response.
                    </p>
                    {/* timeline — card-per-event on a rail, matching the OG request-trace chain */}
                    <div className="border-t border-border pt-4">
                      <GatewayTraceTimeline events={events} />
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">No trace events</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Governance-trail DAG (React Flow + dagre) */}
      <GatewayTraceDag traceId={traceId} open={dagOpen} onClose={() => setDagOpen(false)} />
    </div>
  );
}

function IdItem({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-muted-foreground">{label}:</span>
      <span className="text-xs text-foreground/80 font-mono">{value}</span>
      <button
        onClick={() => onCopy(value)}
        className="p-0.5 rounded hover:bg-primary/[0.025] text-muted-foreground/70 hover:text-muted-foreground"
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}


export default function GatewayAuditPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const reprocessCorr = async (correlationId: string) => {
    try {
      await GatewayPostProcessorApi.reprocess(correlationId);
      toast({ title: "Response classified — open Processed Data to view" });
    } catch (e: any) {
      toast({ title: "Reprocess failed", description: apiError(e), variant: "destructive" });
    }
  };

  const [stats, setStats] = useState<AuditStats | null>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<AuditFilters | null>(null);
  const [pagedResult, setPagedResult] = useState<PagedResult | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [tableLoading, setTableLoading] = useState(false);

  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [correlationEvents, setCorrelationEvents] = useState<any[]>([]);
  const [correlationLoading, setCorrelationLoading] = useState(false);

  const [params, setParams] = useState<QueryParams>({
    page: 0,
    size: 25,
    sort: "timestamp,desc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    Promise.all([
      GatewayAuditApi.stats().then(setStats).catch(console.warn),
      GatewayAuditApi.timeline(24).then(setTimeline).catch(console.warn),
      GatewayAuditApi.filters()
        .then((f) => setFilterOptions(f))
        .catch(console.warn),
    ]);
  }, []);

  const loadLogs = useCallback(async (p: QueryParams) => {
    setTableLoading(true);
    try {
      const result = await GatewayAuditApi.logs(p);
      setPagedResult(result);
    } catch (err: any) {
      toast({ title: "Failed to load audit logs", description: err?.message, variant: "destructive" });
    } finally {
      setTableLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLogs(params);
  }, [params, loadLogs]);

  const filterFields = [
    {
      key: "module",
      label: "Module",
      type: "select" as const,
      options: filterOptions?.modules || [],
    },
    {
      key: "eventType",
      label: "Event Type",
      type: "select" as const,
      options: filterOptions?.eventTypes || [],
    },
    {
      key: "status",
      label: "Status",
      type: "select" as const,
      options: filterOptions?.statuses || [],
    },
    {
      key: "severity",
      label: "Severity",
      type: "select" as const,
      options: filterOptions?.severities || [],
    },
    {
      key: "agentName",
      label: "Agent",
      type: "select" as const,
      options: filterOptions?.agentNames || filterOptions?.agents || [],
    },
    {
      key: "serverName",
      label: "Server",
      type: "select" as const,
      options: filterOptions?.serverNames || filterOptions?.servers || [],
    },
    {
      key: "tokenType",
      label: "Token Type",
      type: "select" as const,
      // send raw enum, show friendly labels
      options: ((filterOptions as any)?.tokenTypes || []).map((t: string) =>
        t === "HUMAN_DELEGATED"
          ? { value: t, label: "Human" }
          : t === "AUTOMATED_AGENT"
            ? { value: t, label: "Automated" }
            : { value: t, label: t }
      ),
    },
    {
      key: "protocol",
      label: "Protocol",
      type: "select" as const,
      options: [
        { value: "MCP", label: "MCP" },
        { value: "A2A", label: "A2A" },
      ],
    },
    {
      key: "capabilityType",
      label: "Capability Type",
      type: "select" as const,
      options: [
        { value: "TOOL", label: "Tool" },
        { value: "PROMPT", label: "Prompt" },
        { value: "RESOURCE", label: "Resource" },
        { value: "SKILL", label: "Skill" },
      ],
    },
    {
      key: "userIdentity",
      label: "User Identity",
      type: "select" as const,
      options: filterOptions?.userIdentities || filterOptions?.humanUsers || [],
    },
    { key: "capabilityName", label: "Capability", type: "text" as const, placeholder: "tool/resource name" },
    { key: "since", label: "Since", type: "date" as const },
    { key: "until", label: "Until", type: "date" as const },
    { key: "sourceIp", label: "IP Address", type: "ip" as const, placeholder: "e.g. 192.168.1.* or 10.0.0.5" },
    { key: "traceId", label: "Trace ID", type: "text" as const, placeholder: "exact trace id" },
    { key: "search", label: "Search", type: "text" as const, placeholder: "correlation, session, user..." },
  ];

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const pickStr = (key: string): string | undefined => {
    const v = filterValues[key];
    if (typeof v === "string" && v.trim()) return v;
    return undefined;
  };
  // backend binds these to a single @RequestParam (honors only the first value), so send one
  const pickFirst = (key: string): string | undefined => {
    const v = filterValues[key];
    if (Array.isArray(v) && v.length > 0) return v[0];
    if (typeof v === "string" && v.trim()) return v;
    return undefined;
  };
  // backend @DateTimeFormat LocalDateTime is zone-less — strip the ISO 'Z'/millis
  const toLocalDateTime = (raw?: string): string | undefined =>
    raw ? new Date(raw).toISOString().slice(0, 19) : undefined;

  const handleApplyFilters = () => {
    const newParams: QueryParams = {
      ...params,
      page: 0,
      module: pickFirst("module"),
      eventType: pickFirst("eventType"),
      status: pickFirst("status"),
      severity: pickFirst("severity"),
      agentName: pickFirst("agentName"),
      serverName: pickFirst("serverName"),
      tokenType: pickFirst("tokenType"),
      protocol: pickFirst("protocol"),
      capabilityType: pickFirst("capabilityType"),
      userIdentity: pickFirst("userIdentity"),
      capabilityName: pickStr("capabilityName"),
      traceId: pickStr("traceId"),
      fromDate: toLocalDateTime(pickStr("since")),
      toDate: toLocalDateTime(pickStr("until")),
      sourceIp: pickStr("sourceIp"),
      search: pickStr("search"),
    };
    setParams(newParams);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setParams({ page: 0, size: params.size, sort: params.sort });
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleShowCorrelation = async (id: string) => {
    setCorrelationId(id);
    setCorrelationLoading(true);
    setCorrelationEvents([]);
    try {
      const events = await GatewayAuditApi.correlation(id);
      setCorrelationEvents(events || []);
    } catch (err: any) {
      toast({ title: "Failed to load correlation chain", description: err?.message, variant: "destructive" });
    } finally {
      setCorrelationLoading(false);
    }
  };

  const goToPage = (p: number) => setParams((prev) => ({ ...prev, page: p }));
  const changePageSize = (size: number) => setParams((prev) => ({ ...prev, page: 0, size }));

  const totalElements = pagedResult?.totalElements || 0;
  const totalPages = pagedResult?.totalPages || 0;
  const currentPage = pagedResult?.number || 0;
  const pageSize = pagedResult?.size || params.size;
  const from = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalElements);

  const content = pagedResult?.content || [];

  return (
    <div className="ag-page ag-page-scroll">
      <div className="ag-stat-grid">
        <GatewayStatCard
          icon={<FileText className="w-5 h-5" />}
          label="Total Events"
          value={(stats?.totalEvents || 0).toLocaleString()}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Errors"
          value={(stats?.errorCount || 0).toLocaleString()}
          subtitle={stats?.errorRate !== undefined ? `${stats.errorRate.toFixed(1)}% error rate` : undefined}
          colorClass="text-red-600 dark:text-red-400"
        />
        <GatewayStatCard
          icon={<Clock className="w-5 h-5" />}
          label="Avg Duration"
          value={formatDuration(stats?.avgDurationMs)}
          colorClass="text-blue-600 dark:text-blue-400"
        />
        <div className="ag-card px-5 py-4">
          <p className="ag-field-label mb-2">By Status</p>
          {stats?.byStatus && Object.keys(stats.byStatus).length > 0 ? (
            <div className="space-y-1">
              {Object.entries(stats.byStatus).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <GatewayStatusBadge status={k} />
                  <span className="text-xs text-foreground/80 font-medium tabular-nums">
                    {(v as number).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/70">No data</p>
          )}
        </div>
      </div>

      <HourlyTimeline data={timeline} />

      <GatewayFilterBar
        fields={filterFields}
        values={filterValues}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <div className="ag-card overflow-hidden flex-shrink-0">
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border/60 bg-card">
              <th className="w-8 px-3 py-2.5" />
              {[
                "Timestamp",
                "Agent",
                "Module",
                "Event Type",
                "Status",
                "Severity",
                "Server",
                "Capability",
                "Duration",
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={10} className="py-12 text-center">
                  <RefreshCw className="w-5 h-5 text-primary animate-spin mx-auto" />
                </td>
              </tr>
            ) : content.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-muted-foreground text-sm">
                  No audit events found — try adjusting your filters
                </td>
              </tr>
            ) : (
              content.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                const isError = log.status === "ERROR" || log.status === "FAILURE";
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      className={cn(
                        "border-b border-border hover:bg-primary/[0.025] cursor-pointer transition-colors",
                        isError && "bg-red-500/5"
                      )}
                      onClick={() => toggleRow(log.id)}
                    >
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-3 py-2.5 text-foreground/80 max-w-[100px] truncate">
                        {log.agentName || "-"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          <GatewayModuleBadge module={log.module || ""} />
                          {log.protocol === "A2A" && <GatewayProtocolBadge protocol="A2A" />}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[220px] truncate">
                        {log.eventType || "-"}
                        {log.comment && <span className="text-muted-foreground/70"> — {log.comment}</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <GatewayStatusBadge status={log.status} />
                      </td>
                      <td className="px-3 py-2.5">
                        <GatewaySeverityDot severity={log.severity || "-"} />
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[120px] truncate">
                        {log.serverName ?? log.serverConfigName ?? "-"}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[160px] truncate">
                        <span className="inline-flex items-center gap-1.5">
                          {log.capabilityName || "-"}
                          {log.capabilityType === "SKILL" && <GatewayCapabilityTypeBadge type="SKILL" />}
                        </span>
                      </td>
                      <td className={cn("px-3 py-2.5 tabular-nums font-medium", durationClass(log.durationMs ?? log.duration))}>
                        {formatDuration(log.durationMs ?? log.duration)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <ExpandedLogRow
                            log={log}
                            onShowCorrelation={handleShowCorrelation}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        </div>

        {!tableLoading && totalElements > 0 && (
          <div className="ag-pagination">
            <span className="ag-pagination-summary">
              Showing {from}–{to} of {totalElements.toLocaleString()}
            </span>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                className="h-8 rounded-lg border border-border/70 bg-card px-2 text-xs text-foreground/80 shadow-[0_1px_3px_rgb(15_23_42/0.04)] outline-none"
              >
                {[10, 25, 50, 100].map((s) => (
                  <option key={s} value={s}>
                    {s} / page
                  </option>
                ))}
              </select>
              <CardPagination
                label="Audit pages"
                currentPage={currentPage + 1}
                totalPages={Math.max(1, totalPages)}
                onPageChange={(p) => goToPage(p - 1)}
              />
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!correlationId} onOpenChange={() => setCorrelationId(null)}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <Share2 />
              </span>
              <div>
                <DialogTitle>Correlation Chain</DialogTitle>
                <DialogDescription>Every event recorded for this request, in order</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            {!correlationLoading && correlationId && (() => {
              const cid = correlationId as string;
              const rp = chainReprocess(correlationEvents);
              return (
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" className={chainLinkCls} onClick={() => postProcNav(navigate, { correlationId: cid })}>
                    <ShieldCheck className="w-3 h-3" /> Processed Data
                  </button>
                  {rp.can && (
                    <button type="button" className={chainLinkCls} onClick={() => reprocessCorr(cid)}>
                      <RefreshCw className="w-3 h-3" /> {rp.label}
                    </button>
                  )}
                </div>
              );
            })()}
            {correlationLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-5 h-5 text-primary animate-spin" />
              </div>
            ) : (
              <>
                {correlationEvents.length > 0 && (() => {
                  const inbound = correlationEvents.find((e: any) => e.module !== "MCP_OUTBOUND" && e.sessionId)?.sessionId;
                  const downstream = correlationEvents.find((e: any) => e.module === "MCP_OUTBOUND" && e.sessionId)?.sessionId;
                  const stateless = typeof inbound === "string" && inbound.startsWith("stateless-");
                  const copyText = (t: string) => { copyToClipboard(t); toast({ title: "Copied to clipboard" }); };
                  return (
                    <div className="rounded-xl border border-border ag-surface px-4 py-3.5">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Correlation ID:{" "}
                          <code className="text-primary font-mono break-all">{correlationId}</code> — {correlationEvents.length} events
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold",
                            stateless ? "text-violet-600 dark:text-violet-400 border-violet-500/40" : "text-emerald-600 dark:text-emerald-400 border-emerald-500/40"
                          )}>
                            {stateless ? "STATELESS" : "STATEFUL"}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {stateless ? "per-request — no agent session held" : "persistent agent session"}
                          </span>
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-dashed border-border space-y-2.5">
                        <ConnSessionRow
                          icon={<Bot className="w-4 h-4 text-primary/80" />}
                          label="Agent → Gateway"
                          sid={inbound}
                          hint="inbound · agent MCP session"
                          stateless={stateless}
                          onCopy={copyText}
                        />
                        <ConnSessionRow
                          icon={<Server className="w-4 h-4 text-teal-600/80 dark:text-teal-400/80" />}
                          label="Gateway → Server"
                          sid={downstream}
                          hint={downstream ? "downstream · gateway's session to the end server" : undefined}
                          onCopy={copyText}
                        />
                      </div>
                    </div>
                  );
                })()}
                <GatewayTimeline events={correlationEvents} />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
